import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Award, Globe, Target, Lightbulb, Shield, TrendingUp, Heart } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

/**
 * AboutPage component - Company information and team showcase
 * Author: VB Entreprise
 * 
 * Features:
 * - Company mission and values
 * - Team member profiles
 * - Awards and certifications
 * - Company statistics and achievements
 */
const AboutPage: React.FC = () => {
  const stats = [
    { number: '500+', label: 'Projects Completed', icon: <Target /> },
    { number: '200+', label: 'Happy Clients', icon: <Users /> },
    { number: '50+', label: 'Team Members', icon: <Users /> },
    { number: '15+', label: 'Countries Served', icon: <Globe /> }
  ];

  const values = [
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: 'Innovation',
      description: 'We stay at the forefront of technology, constantly exploring new solutions to solve complex business challenges.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Reliability',
      description: 'Our clients trust us to deliver secure, scalable solutions that perform consistently under pressure.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Growth',
      description: 'We measure our success by the growth and achievements of our clients and our team members.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Partnership',
      description: 'We build long-term partnerships with our clients, understanding their business as our own.'
    }
  ];

  const team = [
    {
      name: 'Vincent Beaumont',
      role: 'Founder & CEO',
      bio: 'Visionary leader with 15+ years in enterprise technology and digital transformation.',
      image: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=300',
      expertise: ['Strategic Planning', 'Digital Transformation', 'Business Development']
    },
    {
      name: 'Sarah Mitchell',
      role: 'CTO',
      bio: 'Technical architect specializing in scalable cloud solutions and emerging technologies.',
      image: 'https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=300',
      expertise: ['Cloud Architecture', 'DevOps', 'AI/ML']
    },
    {
      name: 'Marcus Johnson',
      role: 'VP of Engineering',
      bio: 'Full-stack development expert leading our technical teams to deliver exceptional solutions.',
      image: 'https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=300',
      expertise: ['Full-Stack Development', 'Team Leadership', 'Agile Methodologies']
    },
    {
      name: 'Emily Chen',
      role: 'Head of Digital Marketing',
      bio: 'Data-driven marketing strategist with expertise in growth hacking and customer acquisition.',
      image: 'https://images.pexels.com/photos/1181519/pexels-photo-1181519.jpeg?auto=compress&cs=tinysrgb&w=300',
      expertise: ['Digital Strategy', 'Growth Marketing', 'Analytics']
    }
  ];

  const certifications = [
    { name: 'Google Ads Display Certified', logoImg: 'https://templates.images.credential.net/16692049902649897014065939581188.png' },
    { name: 'Meta Certified Digital Marketing Associate', logoImg: '/assets/meta-digital-marketing-associate.png' },
    { name: 'Shopify Partner', logoImg: '/assets/shopify partner.PNG' }
  ];

  return (
    <>
      <Helmet>
        <title>About | VB Entreprise | Premium IT & Digital Marketing Agency for US, UK & Canada Businesses</title>
      </Helmet>
      <div className="pt-8">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
                  About <span className="text-blue-600">VB Entreprise</span>
                </h1>
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  Founded with a vision to bridge the gap between innovative technology and business success, VB Entreprise has become a trusted partner for companies worldwide seeking digital transformation and growth.
                </p>
                <Link
                  to="/contact"
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
                >
                  Work With Us
                </Link>
              </div>
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Team collaboration"
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
                <div className="absolute -bottom-4 -right-4 bg-blue-600 text-white p-6 rounded-lg shadow-lg">
                  <div className="text-2xl font-bold">2023</div>
                  <div className="text-sm">Founded</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Mission & Vision</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-blue-50 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Mission</h3>
                <p className="text-gray-700 leading-relaxed">
                  To empower businesses with cutting-edge technology solutions that drive growth, efficiency, and innovation. We believe that the right technology, implemented thoughtfully, can transform any organization and unlock its full potential.
                </p>
              </div>
              <div className="bg-orange-50 p-8 rounded-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Vision</h3>
                <p className="text-gray-700 leading-relaxed">
                  To be the global leader in enterprise technology solutions, recognized for our innovation, reliability, and the exceptional results we deliver for our clients. We envision a world where technology seamlessly serves business objectives.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Statistics */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">By the Numbers</h2>
              <p className="text-xl text-gray-600">
                Our track record speaks for itself.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 text-blue-600">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.number}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Values</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These core values guide everything we do and shape how we work with our clients and each other.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 text-blue-600">
                    {value.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Certifications & Partnerships</h2>
              <p className="text-xl text-gray-600">
                Trusted by industry leaders and certified for excellence.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
              {certifications.map((cert, index) => (
                <div key={index} className="text-center p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg mx-auto mb-3 flex items-center justify-center overflow-hidden">
                    {cert.logoImg ? (
                      <img src={cert.logoImg} alt={cert.name} className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-gray-600 font-semibold text-sm">{cert.name}</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">{cert.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Work Together?
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Join the companies that trust VB Entreprise with their most important technology initiatives.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link
                to="/contact"
                className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Start a Conversation
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default AboutPage;